import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Building2, ChevronRight } from "lucide-react";
import { useApi } from "@/lib/api";
import { vendorTileClass } from "./directory-data";
import { useVendors } from "./use-directory";

type TaggedRef = { id: number; name: string };

type OperationsTask = {
  id: number;
  status: string;
  vendors: TaggedRef[];
};

type Counts = Record<number, { open: number; done: number }>;

export function VendorsPage() {
  const api = useApi();
  const vendorsQ = useVendors();
  const tasksQ = useQuery({
    queryKey: ["operations_tasks"],
    queryFn: async (): Promise<OperationsTask[]> => {
      const res = await api(`/api/operations_tasks`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as OperationsTask[];
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const isLoading = vendorsQ.isLoading || tasksQ.isLoading;

  const counts: Counts = {};
  for (const v of vendorsQ.data ?? []) counts[v.id] = { open: 0, done: 0 };
  for (const row of tasksQ.data ?? []) {
    for (const v of row.vendors ?? []) {
      if (!counts[v.id]) counts[v.id] = { open: 0, done: 0 };
      if (row.status === "done") counts[v.id].done += 1;
      else counts[v.id].open += 1;
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b">
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-violet-500" />
          <div>
            <h1 className="text-xl font-semibold text-foreground">Vendors</h1>
            <p className="text-sm text-muted-foreground">
              Open the page for any supplier to see action items tagged for them.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {(vendorsQ.data ?? []).map((vendor) => {
            const c = counts[vendor.id] ?? { open: 0, done: 0 };
            const tileColor = vendorTileClass(vendor.name, vendor.tile_color);
            return (
              <Link
                key={vendor.id}
                to={`/vendors/${encodeURIComponent(vendor.name)}`}
                className="group rounded-xl border bg-card p-5 hover:border-foreground/20 hover:shadow-sm transition-all flex flex-col gap-3 relative"
              >
                {c.open > 0 && (
                  <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    {c.open}
                  </span>
                )}
                <div
                  className={`rounded-lg border px-3 py-4 text-center font-semibold text-base ${tileColor}`}
                >
                  {vendor.name}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {isLoading ? "…" : `${c.open} open · ${c.done} done`}
                  </span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

(VendorsPage as unknown as { path: string }).path = "/vendors";
